/**
 * <s-hero-slider> — lightweight scroll-snap slider.
 * Markup: [data-slider-track] containing [data-slide] children, optional
 * [data-slider-prev], [data-slider-next] and [data-slider-dot] buttons (data-index).
 * Native swipe/scroll does the moving; this element syncs dots, wires buttons, and
 * auto-advances (paused on hover, focus, hidden tab, or reduced-motion preference).
 */
class SHeroSlider extends HTMLElement {
  #track = null;
  #slides = [];
  #dots = [];
  #index = 0;
  #timer = null;
  #paused = false;
  #observer = null;

  connectedCallback() {
    this.#track = this.querySelector('[data-slider-track]');
    if (!this.#track) return;
    this.#slides = [...this.#track.querySelectorAll('[data-slide]')];
    this.#dots = [...this.querySelectorAll('[data-slider-dot]')];
    if (this.#slides.length < 2) return;

    this.querySelector('[data-slider-prev]')?.addEventListener('click', () => this.go(this.#index - 1));
    this.querySelector('[data-slider-next]')?.addEventListener('click', () => this.go(this.#index + 1));
    this.#dots.forEach((dot) => {
      dot.addEventListener('click', () => this.go(Number(dot.dataset.index)));
    });

    this.#observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            this.#setActive(this.#slides.indexOf(entry.target));
          }
        });
      },
      { root: this.#track, threshold: [0.6] }
    );
    this.#slides.forEach((slide) => this.#observer.observe(slide));

    const pause = () => { this.#paused = true; };
    const resume = () => { this.#paused = false; };
    this.addEventListener('pointerenter', pause);
    this.addEventListener('pointerleave', resume);
    this.addEventListener('focusin', pause);
    this.addEventListener('focusout', resume);
    this.#track.addEventListener('touchstart', pause, { passive: true });

    const interval = Number(this.dataset.interval || 6000);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduceMotion && interval > 0) {
      this.#timer = window.setInterval(() => {
        if (!this.#paused && document.visibilityState === 'visible') this.go(this.#index + 1);
      }, interval);
    }
    this.#setActive(0);
  }

  disconnectedCallback() {
    if (this.#timer) window.clearInterval(this.#timer);
    this.#observer?.disconnect();
  }

  go(index) {
    const count = this.#slides.length;
    const next = ((index % count) + count) % count;
    const slide = this.#slides[next];
    this.#track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    this.#setActive(next);
  }

  #setActive(index) {
    if (index < 0) return;
    this.#index = index;
    this.#slides.forEach((slide, i) => {
      slide.toggleAttribute('inert', i !== index);
      slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
    });
    this.#dots.forEach((dot, i) => {
      dot.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
  }
}

if (!customElements.get('s-hero-slider')) {
  customElements.define('s-hero-slider', SHeroSlider);
}
